import React from 'react';

/**
 * Section 2 : Identité et situation familiale
 */
const AssessmentSection2_Identite = ({ data, beneficiary, onChange }) => {
  const handleChange = (category, field, value) => {
    onChange(category, {
      ...data[category],
      [field]: value
    });
  };

  const handleArrayToggle = (category, field, value) => {
    const currentArray = data[category]?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];

    onChange(category, {
      ...data[category],
      [field]: newArray
    });
  };

  return (
    <div className="assessment-section-content">
      <h3 className="assessment-section-title">IDENTITÉ ET SITUATION FAMILIALE</h3>
      <p className="assessment-section-description">
        Les informations de base du bénéficiaire sont pré-remplies. Complétez les informations sur la situation familiale et la protection juridique.
      </p>

      {/* Informations de base (lecture seule - déjà dans le profil) */}
      <div className="assessment-info-card">
        <p><strong>Nom :</strong> {beneficiary?.lastName || 'Non renseigné'}</p>
        <p><strong>Prénom :</strong> {beneficiary?.firstName || 'Non renseigné'}</p>
        <p><strong>Date de naissance :</strong> {beneficiary?.dateOfBirth || 'Non renseignée'}</p>
        <p><strong>Adresse :</strong> {
          typeof beneficiary?.address === 'object'
            ? `${beneficiary.address.street || ''}, ${beneficiary.address.postalCode || ''} ${beneficiary.address.city || ''}, ${beneficiary.address.country || ''}`.trim()
            : (beneficiary?.address || 'Non renseignée')
        }</p>
        <p><strong>Téléphone :</strong> {beneficiary?.phone || 'Non renseigné'}</p>
        <p><strong>Email :</strong> {beneficiary?.email || 'Non renseigné'}</p>
      </div>

      {/* Situation familiale */}
      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Situation familiale
      </h4>

      <div className="assessment-form-group">
        <label>Statut matrimonial</label>
        <select
          className="assessment-form-control"
          value={data.familialStatus?.maritalStatus || ''}
          onChange={(e) => handleChange('familialStatus', 'maritalStatus', e.target.value)}
        >
          <option value="">-- Sélectionner --</option>
          <option value="celibataire">Célibataire</option>
          <option value="marie">Marié(e) / Pacsé(e)</option>
          <option value="couple">En couple (union libre)</option>
          <option value="veuf">Veuf(ve)</option>
          <option value="divorce">Divorcé(e) / Séparé(e)</option>
        </select>
      </div>

      <div className="assessment-form-group">
        <label>Personnes vivant au domicile</label>
        <div className="assessment-checkbox-group">
          <label className="assessment-checkbox-label">
            <input
              type="checkbox"
              checked={data.familialStatus?.livingWith?.includes('seul') || false}
              onChange={(e) => handleArrayToggle('familialStatus', 'livingWith', 'seul')}
            />
            <span>Seul(e)</span>
          </label>
          <label className="assessment-checkbox-label">
            <input
              type="checkbox"
              checked={data.familialStatus?.livingWith?.includes('conjoint') || false}
              onChange={(e) => handleArrayToggle('familialStatus', 'livingWith', 'conjoint')}
            />
            <span>Avec conjoint(e) / compagnon(compagne)</span>
          </label>
          <label className="assessment-checkbox-label">
            <input
              type="checkbox"
              checked={data.familialStatus?.livingWith?.includes('enfants') || false}
              onChange={(e) => handleArrayToggle('familialStatus', 'livingWith', 'enfants')}
            />
            <span>Avec enfant(s)</span>
          </label>
          <label className="assessment-checkbox-label">
            <input
              type="checkbox"
              checked={data.familialStatus?.livingWith?.includes('parents') || false}
              onChange={(e) => handleArrayToggle('familialStatus', 'livingWith', 'parents')}
            />
            <span>Avec parent(s) / autre famille</span>
          </label>
        </div>
      </div>

      <div className="assessment-grid-2">
        <div className="assessment-form-group">
          <label>Nombre d'enfants</label>
          <input
            type="number"
            min="0"
            className="assessment-form-control"
            value={data.familialStatus?.numberOfChildren || ''}
            onChange={(e) => handleChange('familialStatus', 'numberOfChildren', parseInt(e.target.value) || null)}
          />
        </div>
      </div>

      {/* Protection juridique */}
      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '32px', marginBottom: '16px' }}>
        Protection juridique
      </h4>

      <div className="assessment-form-group">
        <label>Bénéficiez-vous d'une mesure de protection juridique ?</label>
        <div className="assessment-radio-group">
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="hasProtection"
              checked={data.legalProtection?.hasProtection === false}
              onChange={() => handleChange('legalProtection', 'hasProtection', false)}
            />
            <span>Non</span>
          </label>
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="hasProtection"
              checked={data.legalProtection?.hasProtection === true}
              onChange={() => handleChange('legalProtection', 'hasProtection', true)}
            />
            <span>Oui</span>
          </label>
        </div>
      </div>

      {data.legalProtection?.hasProtection && (
        <>
          <div className="assessment-form-group">
            <label>Type de mesure</label>
            <select
              className="assessment-form-control"
              value={data.legalProtection?.protectionType || ''}
              onChange={(e) => handleChange('legalProtection', 'protectionType', e.target.value)}
            >
              <option value="">-- Sélectionner --</option>
              <option value="tutelle">Tutelle</option>
              <option value="curatelle">Curatelle</option>
              <option value="sauvegarde">Sauvegarde de justice</option>
              <option value="mandat">Mandat de protection future</option>
            </select>
          </div>

          <h5 style={{ fontSize: '1rem', fontWeight: 600, marginTop: '20px', marginBottom: '12px' }}>
            Coordonnées du représentant légal / mandataire
          </h5>

          <div className="assessment-form-group">
            <label>Nom et prénom</label>
            <input
              type="text"
              className="assessment-form-control"
              value={data.legalProtection?.representative?.name || ''}
              onChange={(e) => handleChange('legalProtection', 'representative', {
                ...data.legalProtection?.representative,
                name: e.target.value
              })}
            />
          </div>

          <div className="assessment-form-group">
            <label>Organisme (si mandataire professionnel)</label>
            <input
              type="text"
              className="assessment-form-control"
              value={data.legalProtection?.representative?.organization || ''}
              onChange={(e) => handleChange('legalProtection', 'representative', {
                ...data.legalProtection?.representative,
                organization: e.target.value
              })}
            />
          </div>

          <div className="assessment-grid-2">
            <div className="assessment-form-group">
              <label>Téléphone</label>
              <input
                type="tel"
                className="assessment-form-control"
                value={data.legalProtection?.representative?.phone || ''}
                onChange={(e) => handleChange('legalProtection', 'representative', {
                  ...data.legalProtection?.representative,
                  phone: e.target.value
                })}
              />
            </div>

            <div className="assessment-form-group">
              <label>Email</label>
              <input
                type="email"
                className="assessment-form-control"
                value={data.legalProtection?.representative?.email || ''}
                onChange={(e) => handleChange('legalProtection', 'representative', {
                  ...data.legalProtection?.representative,
                  email: e.target.value
                })}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentSection2_Identite;
